import { supabase } from '../lib/supabase';
import { apiPost } from '../lib/api';
import type { Database } from '../lib/database.types';

type BookingInsert = Database['public']['Tables']['bookings']['Insert'];
type BookingRow = Database['public']['Tables']['bookings']['Row'];

export interface CreateBookingParams {
  configId: string;
  courseId: string;
  slotStart: string;
  playerId: string;
  qty: number;
  priceCents: number;
  bookingType: 'auto' | 'request';
  refundPolicyId: string | null;
  refundHoursSnapshot: number | null;
}

export interface BookingResult {
  id: string;
  confirmationNumber: string;
  configId: string;
  courseId: string;
  slotStart: string;
  playerId: string;
  qty: number;
  totalDollarAmount: number;
  ownerAmount: number;
  bookingFeeAmount: number;
  status: string;
  createdAt: string;
}

function generateConfirmationNumber(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 5; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

export type CreateBookingError = 'SLOT_TAKEN' | 'GROUP_TOO_LARGE' | 'COURSE_ERROR' | 'INSERT_ERROR';

// Fallback rate used only if platform_config cannot be reached.
const DISTRO_FUND_RATE_FALLBACK = 0.01;

async function getDistroFundRate(): Promise<number> {
  const { data, error } = await supabase
    .from('platform_config')
    .select('value')
    .eq('key', 'distro_fund_rate')
    .single();
  if (error || !data) return DISTRO_FUND_RATE_FALLBACK;
  const rate = parseFloat(data.value);
  return isNaN(rate) ? DISTRO_FUND_RATE_FALLBACK : rate;
}

export async function createBooking(
  params: CreateBookingParams
): Promise<BookingResult | { error: CreateBookingError } | null> {
  const { configId, courseId, slotStart, playerId, qty, priceCents, bookingType, refundPolicyId, refundHoursSnapshot } = params;

  // Check if this slot already has an active booking
  const { data: existing, error: existingError } = await supabase
    .from('bookings')
    .select('id')
    .eq('tee_time_config_id', configId)
    .eq('slot_start', slotStart)
    .in('status', ['confirmed', 'requested', 'reserved', 'paid', 'pending_payment'])
    .limit(1);

  if (!existingError && existing && existing.length > 0) {
    return { error: 'SLOT_TAKEN' };
  }

  // Validate group size against the config's max
  const { data: config, error: configError } = await supabase
    .from('tee_time_configs')
    .select('max_players_per_slot')
    .eq('id', configId)
    .single();

  if (!configError && config && qty > config.max_players_per_slot) {
    return { error: 'GROUP_TOO_LARGE' };
  }

  // Fetch the course to get the booking fee percentage
  const { data: course, error: courseError } = await supabase
    .from('courses')
    .select('booking_fee_percent')
    .eq('id', courseId)
    .single();

  if (courseError || !course) {
    console.error('Error fetching course:', courseError);
    return { error: 'COURSE_ERROR' };
  }

  const totalDollarAmount = (priceCents * qty) / 100;
  const bookingFeePercent = course.booking_fee_percent ?? 0;
  const bookingFeeAmount = (totalDollarAmount * bookingFeePercent) / 100;
  const ownerAmount = totalDollarAmount - bookingFeeAmount;
  const distroFundRate = await getDistroFundRate();
  const distroFundContribution = bookingFeeAmount * distroFundRate;

  const bookingData: BookingInsert & { refund_policy_id?: string | null; refund_hours_snapshot?: number | null; distro_fund_contribution?: number } = {
    tee_time_config_id: configId,
    course_id: courseId,
    slot_start: slotStart,
    player_id: playerId,
    qty,
    total_dollar_amount: totalDollarAmount,
    owner_amount: ownerAmount,
    booking_fee_amount: bookingFeeAmount,
    distro_fund_contribution: distroFundContribution,
    status: bookingType === 'auto' ? 'confirmed' : 'requested',
    refund_policy_id: refundPolicyId,
    refund_hours_snapshot: refundHoursSnapshot,
    confirmation_number: generateConfirmationNumber(),
  };

  const { data, error } = await supabase
    .from('bookings')
    .insert(bookingData as any)
    .select()
    .single();

  if (error || !data) {
    console.error('Error creating booking:', error);
    return { error: 'INSERT_ERROR' };
  }

  const typedData = data as BookingRow;

  return {
    id: typedData.id,
    confirmationNumber: (typedData as any).confirmation_number || typedData.id.slice(0, 5).toUpperCase(),
    configId: typedData.tee_time_config_id,
    courseId: typedData.course_id,
    slotStart: typedData.slot_start,
    playerId: typedData.player_id,
    qty: typedData.qty,
    totalDollarAmount: typedData.total_dollar_amount,
    ownerAmount: typedData.owner_amount,
    bookingFeeAmount: typedData.booking_fee_amount,
    status: typedData.status,
    createdAt: typedData.created_at || new Date().toISOString()
  };
}

export async function cancelBooking(bookingId: string): Promise<boolean> {
  try {
    await apiPost('/api/bookings/cancel', { bookingId });
    return true;
  } catch (err) {
    console.error('Error canceling booking:', err);
    return false;
  }
}
